import type { ReactElement, ReactNode } from "react";
import { cn } from "../lib/utils.js";
import { SHAPE_CLASS, type Shape } from "../lib/shape.js";
import { FLOATING_SURFACE, GLASS_SURFACE } from "../lib/surfaces.js";
import type { Rect } from "./container-geometry.js";

/* A scaled overview of a canvas: the caller's rects drawn in a small floating
   frame. Data-blind: it knows no widget, note, phase or document. A caller
   turns its own data into Rect[] layers and builds `frame` (usually with
   `boundingBox` plus its own padding); the part only projects and paints.

   The frame's paint comes from lib/surfaces.ts and its corner from
   lib/shape.ts; the part writes no fill, border or shadow class of its own for
   the frame. The p-2 inset and the inner map (rounded-md, hairline, page fill)
   are fixed. `className` places the frame (position, inset, z-index, margin).
   It comes first in cn(), so it cannot change the paint, padding or corner. */

/** contain: one scale, the slack centred. fill: x and y scale apart. */
export type CanvasMiniMapFit = "contain" | "fill";

/** The frame's paint (design-owner decision 7: both floating paints stay). */
export type CanvasMiniMapSurface = "default" | "glass";

/** The frame's corner, from the shared C7 words (lib/shape.ts). */
export type CanvasMiniMapShape = Extract<Shape, "card" | "panel" | "rounded">;

/** What a rect is. Each kind has one paint; it is not a visual variant. */
export type CanvasMiniMapLayerKind = "item" | "note" | "region";

export interface CanvasMiniMapLayer {
  /** Unique among the layers; also written on each rect as `data-layer`. */
  readonly id: string;
  readonly kind: CanvasMiniMapLayerKind;
  /** Rects in canvas units. */
  readonly rects: readonly Rect[];
}

/** Maps a rect in canvas units to a rect in mini-map pixels. */
export type CanvasMiniMapProject = (rect: Rect) => Rect;

export interface CanvasMiniMapProps {
  /** The canvas area the map shows, in canvas units. */
  readonly frame: Rect;
  /** Current visible canvas area, drawn above the content. */
  readonly viewport?: Rect | undefined;
  /** The inner map's width in pixels. */
  readonly width: number;
  /** The inner map's height in pixels. */
  readonly height: number;
  /** "contain" (default) scales by min(width / frame.w, height / frame.h) and
   *  centres the slack. "fill" scales x and y apart, so the frame fills the
   *  map. */
  readonly fit?: CanvasMiniMapFit | undefined;
  /** Painted in order: a later layer draws over an earlier one. */
  readonly layers: readonly CanvasMiniMapLayer[];
  /** Drawn beneath the layers (lanes, zones). It receives the projection the
   *  layers use, so it lines up with them. */
  readonly renderUnderlay?:
    | ((project: CanvasMiniMapProject) => ReactNode)
    | undefined;
  /** "default" is the workspace paint; "glass" the see-through paint for a
   *  map over a canvas. */
  readonly surface?: CanvasMiniMapSurface | undefined;
  /** The frame's corner: "card" 14px (default), "panel" 10px, "rounded" 8px. */
  readonly shape?: CanvasMiniMapShape | undefined;
  /** The map's accessible name (role="img"). Absent, the map is aria-hidden:
   *  it repeats what the canvas already exposes. */
  readonly label?: string | undefined;
  readonly "data-testid"?: string | undefined;
  /** Placement only: position, inset, z-index, margin. */
  readonly className?: string | undefined;
}

const SURFACE_PAINT: Record<CanvasMiniMapSurface, string> = {
  default: FLOATING_SURFACE,
  glass: GLASS_SURFACE,
};

/* Today's token paints: item is the Hoshin and SOLVE widget rect, note the
   sticky-note tokens (SOLVE), region the VSM container outline. */
const KIND_PAINT: Record<CanvasMiniMapLayerKind, string> = {
  item: "border-pb-cyan/70 bg-pb-cyan/25",
  note: "border-pb-sticky-line/70 bg-pb-sticky/50",
  region: "border-pb-border-strong/50",
};

/** The smallest side a layer rect draws at, so a tiny item stays visible. */
const MIN_RECT_PX = 2;

function projection(
  frame: Rect,
  width: number,
  height: number,
  fit: CanvasMiniMapFit,
): CanvasMiniMapProject {
  // A zero-size frame (one unmeasured rect) scales to 0, never divides by 0.
  const sx = frame.w > 0 ? width / frame.w : 0;
  const sy = frame.h > 0 ? height / frame.h : 0;
  const scaleX = fit === "fill" ? sx : Math.min(sx, sy);
  const scaleY = fit === "fill" ? sy : scaleX;
  const offsetX = fit === "fill" ? 0 : (width - frame.w * scaleX) / 2;
  const offsetY = fit === "fill" ? 0 : (height - frame.h * scaleY) / 2;
  return (rect) => ({
    x: offsetX + (rect.x - frame.x) * scaleX,
    y: offsetY + (rect.y - frame.y) * scaleY,
    w: rect.w * scaleX,
    h: rect.h * scaleY,
  });
}

/** Project caller-owned layers and the viewport into one frame without reading or mutating canvas data. */
export function CanvasMiniMap({
  frame,
  viewport,
  width,
  height,
  fit = "contain",
  layers,
  renderUnderlay,
  surface = "default",
  shape = "card",
  label,
  "data-testid": testId,
  className,
}: CanvasMiniMapProps): ReactElement {
  const project = projection(frame, width, height, fit);
  const a11y = label
    ? ({ role: "img", "aria-label": label } as const)
    : ({ "aria-hidden": true } as const);
  return (
    <div
      data-testid={testId}
      {...a11y}
      className={cn(
        className,
        SURFACE_PAINT[surface],
        "p-2",
        SHAPE_CLASS[shape],
      )}
    >
      <div
        className="relative overflow-hidden rounded-md border border-pb-border bg-pb-page"
        style={{ width, height }}
      >
        {renderUnderlay?.(project)}
        {layers.flatMap((layer) =>
          layer.rects.map((rect, index) => {
            const box = project(rect);
            return (
              <div
                key={`${layer.id}-${index}`}
                data-layer={layer.id}
                className={`absolute rounded-[2px] border ${KIND_PAINT[layer.kind]}`}
                style={{
                  left: box.x,
                  top: box.y,
                  width: Math.max(MIN_RECT_PX, box.w),
                  height: Math.max(MIN_RECT_PX, box.h),
                }}
              />
            );
          }),
        )}
        {viewport
          ? (() => {
              const box = project(viewport);
              return (
                <div
                  data-layer="viewport"
                  className="pointer-events-none absolute border-2 border-pb-accent"
                  style={{
                    left: box.x,
                    top: box.y,
                    width: box.w,
                    height: box.h,
                  }}
                />
              );
            })()
          : null}
      </div>
    </div>
  );
}
